import React, { useState } from 'react';
import SportsbookLogo, { SPORTSBOOK_NAMES, SPORTSBOOK_URLS } from './SportsbookLogo';
import { ExternalLink, Pencil } from 'lucide-react';
import TeamLogo from './TeamLogo';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

function formatOdds(odds) {
  if (odds == null) return '—';
  return odds > 0 ? `+${odds}` : `${odds}`;
}

function oddsToDecimal(val) {
  if (val === null) return 2.0;
  if (val > 0) return val / 100 + 1;
  return 100 / Math.abs(val) + 1;
}

function getSportIcon(sportKey) {
  if (!sportKey) return '🏆';
  if (sportKey.includes('football')) return '🏈';
  if (sportKey.includes('basketball')) return '🏀';
  if (sportKey.includes('hockey')) return '🏒';
  if (sportKey.includes('soccer')) return '⚽';
  if (sportKey.includes('tennis')) return '🎾';
  if (sportKey.includes('mma')) return '🥊';
  if (sportKey.includes('baseball')) return '⚾';
  if (sportKey.includes('boxing')) return '🥊';
  return '🏆';
}

function getMarketLabel(market) {
  if (!market || market === 'h2h') return 'Moneyline';
  if (market === 'spreads') return 'Spread';
  if (market === 'totals') return 'Total';
  if (market === 'props') return 'Props';
  return market;
}

// Kelly criterion stake, capped at the promo's max bet amount.
// Returns { stake, isKelly } — isKelly=false means bankroll not set, showing promo max only.
function calcSuggestedStake(bb, promoAmount, bankroll, kellyFraction) {
  const maxBet = promoAmount || 0;
  if (!bankroll || bankroll <= 0 || !maxBet) return { stake: maxBet, isKelly: false };

  const p = (bb.fair_probability || 50) / 100;
  const q = 1 - p;
  const effectiveOdds = bb.boosted_odds ?? bb.promo_odds;
  const b = oddsToDecimal(effectiveOdds) - 1; // net profit per $1 staked

  if (b <= 0) return { stake: maxBet, isKelly: false };

  const fullKelly = (p * b - q) / b;
  if (fullKelly <= 0) {
    // Negative Kelly = no pure edge, but promo still has value — bet the max
    return { stake: maxBet, isKelly: false };
  }

  const raw = bankroll * (kellyFraction || 0.25) * fullKelly;
  const stake = Math.min(Math.round(raw * 100) / 100, maxBet);
  return { stake, isKelly: true };
}

function BestBetCard({ opportunity, promo, bankroll, kellyFraction, session, qualifyingBetId, onTracked, noteStage, promoId }) {
  const bb = opportunity;
  const sportIcon = getSportIcon(bb.sport);
  const isBoost = bb.boosted_odds != null;
  const isPositive = bb.edge_pct > 0;
  const effectiveOdds = bb.boosted_odds ?? bb.promo_odds;

  const [tracked, setTracked] = useState(false);
  const [trackingMode, setTrackingMode] = useState(false);
  const [amount, setAmount] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editedOdds, setEditedOdds] = useState('');

  const parsedEdit = parseInt(editedOdds);
  const activeOdds = editMode && !isNaN(parsedEdit) && parsedEdit !== 0 ? parsedEdit : effectiveOdds;
  const activeEdgePct = editMode && !isNaN(parsedEdit) && parsedEdit !== 0 && bb.fair_odds
    ? ((oddsToDecimal(activeOdds) / oddsToDecimal(bb.fair_odds)) - 1) * 100
    : bb.edge_pct;
  const activeIsPositive = activeEdgePct > 0;

  const { stake, isKelly } = calcSuggestedStake(
    { ...bb, boosted_odds: editMode && !isNaN(parsedEdit) && parsedEdit !== 0 ? parsedEdit : bb.boosted_odds },
    promo?.amount, bankroll, kellyFraction
  );

  async function handleTrack(e) {
    e.stopPropagation();
    if (!session) return;
    // Determine raw side for auto-resolve (strip spread annotation from promo_side)
    const rawSide = bb.market === 'totals'
      ? (bb.promo_side?.startsWith('Over') ? 'Over' : 'Under')
      : bb.market === 'spreads'
      ? (bb.home_team && bb.promo_side?.startsWith(bb.home_team) ? bb.home_team : bb.away_team) || bb.promo_side
      : bb.promo_side;
    try {
      await fetch(`${API_BASE}/user/bets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({
          betType: 'promo',
          sportsbook: bb.promo_book,
          eventName: bb.event,
          sportKey: bb.sport,
          market: bb.market || 'h2h',
          side: bb.promo_side,
          odds: activeOdds,
          amount: parseFloat(amount) || 0,
          notes: JSON.stringify({
            event_id: bb.event_id,
            sport_key: bb.sport,
            home_team: bb.home_team,
            away_team: bb.away_team,
            side: rawSide,
            point: bb.point ?? null,
            commence_time: bb.commence_time,
            promo_type: bb.type,
            ...(qualifyingBetId
              ? { stage: 'bonus', qualifying_bet_id: qualifyingBetId }
              : noteStage ? { stage: noteStage, ...(promoId ? { promo_id: promoId } : {}) } : {}),
          }),
        }),
      });
      setTrackingMode(false);
      setTracked(true);
      if (onTracked) onTracked();
    } catch (err) {
      console.error('Track failed:', err);
    }
  }

  return (
    <div className="best-bet-card">
      {/* Header */}
      <div className="bbc-header">
        <span className="bbc-sport">{sportIcon} {bb.sport_title || 'Sports'}</span>
        <span className="bbc-market">{getMarketLabel(bb.market)}</span>
      </div>

      {/* Event */}
      <div className="bbc-event">
        <span className="mini-game-team"><TeamLogo teamName={bb.home_team} sportKey={bb.sport} size={13} />{bb.home_team}</span>
        <span className="mini-game-sep">vs</span>
        <span className="mini-game-team"><TeamLogo teamName={bb.away_team} sportKey={bb.sport} size={13} />{bb.away_team}</span>
      </div>
      {bb.commence_time && (
        <div className="pc-game-time">
          {new Date(bb.commence_time).toLocaleString('en-US', {
            weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
          })}
        </div>
      )}

      {/* Edge vs fair value */}
      <div className={`bbc-edge ${activeIsPositive ? 'bbc-edge-positive' : 'bbc-edge-negative'}`}>
        <span className="bbc-edge-pct">
          {activeIsPositive ? '+' : ''}{activeEdgePct.toFixed(1)}% edge vs fair
        </span>
        <span className="bbc-edge-compare">
          Book: {formatOdds(activeOdds)} · Fair: {formatOdds(bb.fair_odds)}
        </span>
      </div>

      {/* Bet row */}
      <div className="bbc-bet-row">
        <div className="bbc-book-row">
          <SportsbookLogo bookKey={bb.promo_book} size={16} />
          <span className="bbc-book-name">{SPORTSBOOK_NAMES[bb.promo_book] || bb.promo_book_title}</span>
          {SPORTSBOOK_URLS[bb.promo_book] && (
            <a href={SPORTSBOOK_URLS[bb.promo_book]} target="_blank" rel="noopener noreferrer" className="book-link" onClick={e => e.stopPropagation()}>
              <ExternalLink size={11} />
            </a>
          )}
        </div>
        <div className="bbc-bet-detail">
          <span className="bbc-side">{bb.promo_side}</span>
          <div className="bbc-odds-wrap">
            {editMode ? (
              <input
                className="odds-edit-input"
                value={editedOdds}
                onChange={e => setEditedOdds(e.target.value)}
                placeholder={String(effectiveOdds)}
                onClick={e => e.stopPropagation()}
              />
            ) : isBoost ? (
              <>
                <span className="bbc-odds-orig">{formatOdds(bb.promo_odds)}</span>
                <span className="bbc-odds-arrow">→</span>
                <span className="bbc-odds-boosted">{formatOdds(bb.boosted_odds)}</span>
              </>
            ) : (
              <span className="bbc-odds">{formatOdds(bb.promo_odds)}</span>
            )}
          </div>
        </div>
      </div>

      {/* Player info for props */}
      {bb.market === 'props' && bb.player_team && (
        <div className="ev-mini-player-info">
          {bb.player_jersey && <span className="ev-mini-player-jersey">#{bb.player_jersey}</span>}
          <TeamLogo teamName={bb.player_team} sportKey={bb.sport} size={13} />
          <span className="ev-mini-player-team">{bb.player_team}</span>
        </div>
      )}

      {/* Suggested stake */}
      {stake > 0 && (
        <div className="bbc-stake-row">
          <span className="bbc-stake-label">Bet</span>
          <span className="bbc-stake-amount">${Math.round(stake)}</span>
          {isKelly && <span className="bbc-stake-note">Kelly</span>}
          {!isKelly && promo?.amount && <span className="bbc-stake-note">max</span>}
        </div>
      )}

      <div className="edit-odds-row">
        {!editMode && <span className="edit-odds-hint">Seeing different odds?</span>}
        <button
          className="edit-odds-btn"
          onClick={e => {
            e.stopPropagation();
            if (editMode) setEditedOdds('');
            setEditMode(v => !v);
          }}
        >
          <Pencil size={11} />
          {editMode ? 'Reset odds' : 'Edit here'}
        </button>
      </div>

      {session && !tracked && !trackingMode && (
        <button
          className="ev-track-btn"
          onClick={(e) => { e.stopPropagation(); setAmount(stake ? String(Math.round(stake)) : ''); setTrackingMode(true); }}
        >
          Track Bet
        </button>
      )}
      {session && trackingMode && (
        <div className="track-amount-form" onClick={e => e.stopPropagation()}>
          <input
            type="number"
            className="track-amount-input"
            value={amount}
            onChange={e => setAmount(e.target.value)}
            placeholder="Stake ($)"
            min="0"
            step="1"
            autoFocus
          />
          <button className="track-amount-save" onClick={handleTrack}>Save</button>
          <button className="track-amount-cancel" onClick={(e) => { e.stopPropagation(); setTrackingMode(false); }}>✕</button>
          {parseFloat(amount) > 0 && (() => {
            const profit = parseFloat(amount) * (activeOdds > 0 ? activeOdds / 100 : 100 / Math.abs(activeOdds));
            return <span className="ctf-to-win">To win: +${profit.toFixed(2)}</span>;
          })()}
        </div>
      )}
      {session && tracked && (
        <button className="ev-track-btn ev-track-btn--done" onClick={e => e.stopPropagation()} disabled>Tracked ✓</button>
      )}
    </div>
  );
}

export default BestBetCard;
